import React, { useEffect, useState } from "react";
import { Loader2, Clock, CalendarClock } from "lucide-react";

// Catalog Fulfillment Scheduling Phase 2 (Sep 2026) — the customer's
// ASAP-vs-later choice.
//
// Every window rendered here came from the backend. The component never
// synthesises a time, never adds a lead time, and never decides whether a
// window is valid — and the canonical `start` instant it hands back is
// what checkout submits and revalidates, NOT the human-readable label.
// A label is for a person to read; the instant is what the server agrees
// on.
//
// Phase 2 scope is Pickup and Takeout; the parent only mounts this when
// the backend says the mode is in scope.
//
// Gate C6 (Sep 2026) adds scheduled Uber DELIVERY. Two differences, both
// driven by `availability.scheduledDelivery` rather than by anything this
// component decides:
//
//   * ASAP delivery is ALWAYS offerable. The catalog's Pickup/Takeout
//     counter hours do not govern delivery, and the backend accepts an
//     ASAP delivery order through them, so greying ASAP out here would
//     block an order the server would have taken.
//   * Delivery windows are same-day and close an hour before the window
//     starts. The copy says so, because a customer who cannot see why
//     the next two hours are missing will assume the site is broken.

function formatDateChip(iso, timezone) {
  // Parse as a plain calendar date. `new Date("2026-09-15")` is parsed as
  // UTC midnight, which renders as the PREVIOUS day for anyone west of
  // Greenwich — the exact bug this feature exists to avoid.
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return iso;
  const local = new Date(y, m - 1, d);
  const today = new Date();
  const isToday = local.toDateString() === today.toDateString();
  const tomorrow = new Date(today.getTime() + 86400000);
  const isTomorrow = local.toDateString() === tomorrow.toDateString();
  if (isToday) return "Today";
  if (isTomorrow) return "Tomorrow";
  return local.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function FulfillmentTimePicker({
  availability,
  value,            // { start, end, label, date } | null  -> null means ASAP
  onChange,
  labelForMode,
}) {
  const {
    asapAvailable, canSchedule, earliest, leadTimeMinutes, timezone, dates, loadWindows,
    windowsVersion,
  } = availability;
  const scheduledDelivery = availability?.scheduledDelivery || { available: false };
  const isScheduledDelivery = !!scheduledDelivery.available;
  // Delivery ASAP is never gated by this catalog's fulfillment hours.
  const asapOfferable = asapAvailable || isScheduledDelivery;

  const availableDates = (dates || []).filter((d) => d.available);
  const [selectedDate, setSelectedDate] = useState(null);
  const [windows, setWindows] = useState([]);
  const [loadingWindows, setLoadingWindows] = useState(false);
  const scheduling = value !== null;

  // Default the date picker to the first date that actually has windows,
  // so a customer who chooses "Schedule for later" never lands on a
  // greyed-out day.
  useEffect(() => {
    if (scheduling && !selectedDate && availableDates.length) {
      setSelectedDate(availableDates[0].date);
    }
  }, [scheduling, selectedDate, availableDates]);

  useEffect(() => {
    let alive = true;
    if (!scheduling || !selectedDate) { setWindows([]); return undefined; }
    setLoadingWindows(true);
    loadWindows(selectedDate).then((rows) => {
      if (!alive) return;
      setWindows(rows);
      setLoadingWindows(false);
    });
    return () => { alive = false; };
    // `windowsVersion` changes on every fresh answer, including the
    // periodic re-read. Without it this list would be drawn once and
    // keep offering a window whose cutoff has since passed.
  }, [scheduling, selectedDate, loadWindows, windowsVersion]);

  if (!asapOfferable && !canSchedule) {
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        data-testid="retail-fulfillment-none"
        role="status"
      >
        <p className="font-semibold">
          Currently unavailable for {labelForMode || "pickup"}.
        </p>
        {earliest
          ? <p className="mt-0.5">Earliest available: {earliest.label} on {earliest.local_date}.</p>
          : <p className="mt-0.5">Please check back during our fulfillment hours.</p>}
      </div>
    );
  }

  const chooseAsap = () => onChange(null);
  const chooseSchedule = () => {
    if (!canSchedule) return;
    onChange(value || { start: "", end: "", label: "", date: "" });
  };

  return (
    <div className="space-y-2" data-testid="retail-fulfillment-time-picker">
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!asapOfferable}
          onClick={chooseAsap}
          data-testid="retail-fulfillment-asap"
          className={
            "flex-1 rounded-lg border px-3 py-2 text-left text-sm transition " +
            (!asapOfferable
              ? "border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed"
              : !scheduling
              ? "border-orange-500 bg-orange-50 text-stone-900"
              : "border-stone-300 hover:border-stone-400")
          }
        >
          <span className="flex items-center gap-1.5 font-medium">
            <Clock className="h-3.5 w-3.5" /> ASAP
          </span>
          <span className="mt-0.5 block text-xs text-stone-500">
            {asapOfferable
              ? (leadTimeMinutes && !isScheduledDelivery
                  ? `Ready in about ${leadTimeMinutes} min`
                  : "As soon as possible")
              : "Not available right now"}
          </span>
        </button>

        <button
          type="button"
          disabled={!canSchedule}
          onClick={chooseSchedule}
          data-testid="retail-fulfillment-schedule"
          className={
            "flex-1 rounded-lg border px-3 py-2 text-left text-sm transition " +
            (!canSchedule
              ? "border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed"
              : scheduling
              ? "border-orange-500 bg-orange-50 text-stone-900"
              : "border-stone-300 hover:border-stone-400")
          }
        >
          <span className="flex items-center gap-1.5 font-medium">
            <CalendarClock className="h-3.5 w-3.5" /> Schedule for later
          </span>
          <span className="mt-0.5 block text-xs text-stone-500">
            {canSchedule
              ? (isScheduledDelivery ? "Pick a time today" : "Pick a day and time")
              : (isScheduledDelivery ? "No times left today" : "Not available")}
          </span>
        </button>
      </div>

      {/* Every window the backend still lists for today sits inside the
          ordering cutoff (or past the horizon), so there is nothing left
          to schedule. Say so plainly: a disabled button with no reason
          reads as a broken site. ASAP delivery is unaffected — it is not
          governed by the cutoff — so the customer is not stuck. */}
      {isScheduledDelivery && !canSchedule && (
        <p className="text-xs text-amber-800" data-testid="retail-fulfillment-delivery-closed">
          No delivery times left today — ordering closes{" "}
          {scheduledDelivery.ordering_cutoff_minutes || 60} minutes before a delivery
          window. You can still order ASAP.
        </p>
      )}

      {isScheduledDelivery && canSchedule && (
        <p className="text-xs text-stone-500" data-testid="retail-fulfillment-delivery-constraints">
          Scheduled delivery is available today, up to{" "}
          {scheduledDelivery.max_horizon_hours || 8} hours ahead. Orders close{" "}
          {scheduledDelivery.ordering_cutoff_minutes || 60} minutes before the delivery window.
        </p>
      )}

      {!asapAvailable && !isScheduledDelivery && canSchedule && (
        <p className="text-xs text-amber-800" data-testid="retail-fulfillment-asap-closed">
          Not available for immediate {labelForMode || "pickup"} right now
          {earliest ? ` — earliest is ${earliest.label} on ${earliest.local_date}` : ""}.
          Choose a time below.
        </p>
      )}

      {scheduling && (
        <div className="rounded-lg border border-stone-200 p-2.5">
          <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="retail-fulfillment-dates">
            {availableDates.map((d) => (
              <button
                key={d.date}
                type="button"
                onClick={() => { setSelectedDate(d.date); onChange({ start: "", end: "", label: "", date: d.date }); }}
                data-testid={`retail-fulfillment-date-${d.date}`}
                className={
                  "shrink-0 rounded-md border px-2.5 py-1 text-xs " +
                  (selectedDate === d.date
                    ? "border-orange-500 bg-orange-50 font-medium"
                    : "border-stone-300 hover:border-stone-400")
                }
              >
                {formatDateChip(d.date, timezone)}
              </button>
            ))}
          </div>

          <div className="mt-2">
            {loadingWindows ? (
              <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
            ) : windows.length ? (
              <div className="flex flex-wrap gap-1.5" data-testid="retail-fulfillment-windows">
                {windows.map((w) => (
                  <button
                    key={w.start}
                    type="button"
                    // The canonical instant is what is stored and submitted.
                    onClick={() => onChange({
                      start: w.start, end: w.end, label: w.label, date: w.local_date,
                    })}
                    data-testid={`retail-fulfillment-window-${w.start}`}
                    className={
                      "rounded-md border px-2 py-1 text-xs " +
                      (value?.start === w.start
                        ? "border-orange-500 bg-orange-50 font-medium"
                        : "border-stone-300 hover:border-stone-400")
                    }
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-500">No times left on that day.</p>
            )}
          </div>

          {value?.start && (
            <p className="mt-2 text-xs font-medium text-stone-800" data-testid="retail-fulfillment-selected">
              Scheduled {labelForMode || "pickup"}: {formatDateChip(value.date, timezone)}, {value.label}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
